import { z } from "zod";

// TES-1284 (TES-1269 point 6) — "Kontrollera provsvar i Webdoc".
//
// Canonical source: "KANONISKT TILLÄGG 2026-10-12 — SEX TIMMARS ÅTERKONTROLL
// AV PROVSVAR" (identical in CP-06, 09C and A-02):
// "När patienten har bekräftat blodprovet och övriga begärda patienthandlingar är klara visas samma ärende för Kirill som: ”Kontrollera provsvar i Webdoc”"
//
// There is no Webdoc integration, so Ella cannot know that a result has
// arrived. The doctor looks in Webdoc and answers with one of three choices.
// Every state below is a state of the SAME work-queue item (the case's
// LabOrder): no parallel item is ever created, and the re-check six hours
// later is computed on read, not scheduled.

export const LAB_RESULT_CHECK_TITLE = "Kontrollera provsvar i Webdoc";
export const LAB_RESULT_RECHECK_TITLE = "Kontrollera provsvar igen";
export const LAB_RESULT_WAITING_TITLE = "Inväntar provsvar";
export const LAB_RESULT_MISSING_TITLE =
  "Provsvar saknas – kontrollera provtagning eller remiss";

// Where "Provsvar finns" leads once every other required item is in: the
// completion round's single item, before the treatment decision.
export const COMPLETION_ROUND_DECISION_TITLE =
  "Kompletteringar klara – fatta behandlingsbeslut";

export const LAB_RESULT_CHECK_OUTCOMES = [
  "results_available",
  "not_yet",
  "problem",
] as const;
export const labResultCheckOutcomeSchema = z.enum(LAB_RESULT_CHECK_OUTCOMES);
export type LabResultCheckOutcome = z.infer<typeof labResultCheckOutcomeSchema>;

export const LAB_RESULT_CHECK_OUTCOME_LABEL: Record<
  LabResultCheckOutcome,
  string
> = {
  results_available: "Provsvar finns",
  not_yet: "Provsvar har inte kommit ännu",
  problem: "Problem med remiss eller provtagning",
};

export const labResultCheckBodySchema = z
  .object({ outcome: labResultCheckOutcomeSchema })
  .strict();
export type LabResultCheckBody = z.infer<typeof labResultCheckBodySchema>;

// check     — first check: "Kontrollera provsvar i Webdoc"
// recheck   — six hours after a "not yet": "Kontrollera provsvar igen"
// waiting   — inside the six hours: "Inväntar provsvar", out of the active list
// escalated — "Provsvar saknas – kontrollera provtagning eller remiss".
//             Not a treatment decline.
export const LAB_RESULT_CHECK_STATES = [
  "check",
  "recheck",
  "waiting",
  "escalated",
] as const;
export const labResultCheckStateSchema = z.enum(LAB_RESULT_CHECK_STATES);
export type LabResultCheckState = z.infer<typeof labResultCheckStateSchema>;

// What the queue card shows: "Kökortet visar patientens uppgivna provtagningsdatum, senaste kontrollen och antal genomförda kontroller."
export const labResultCheckSchema = z.object({
  state: labResultCheckStateSchema,
  // Her own "Jag har lämnat blodprovet" — a self-report, not a result.
  sampleReportedAt: z.string().nullable(),
  lastCheckAt: z.string().nullable(),
  checkCount: z.number().int().nonnegative(),
  // When the item comes back as "Kontrollera provsvar igen". Null before
  // the first "not yet".
  nextCheckAt: z.string().nullable(),
  // Set when the doctor chose "Problem med remiss eller provtagning".
  problemReportedAt: z.string().nullable(),
});
export type LabResultCheck = z.infer<typeof labResultCheckSchema>;

export const labResultCheckResultSchema = z.object({
  orderId: z.string(),
  outcome: labResultCheckOutcomeSchema,
  check: labResultCheckSchema.nullable(),
});
export type LabResultCheckResult = z.infer<typeof labResultCheckResultSchema>;

// TES-1401 (Ö-473, Emelie 2026-10-15) — the operational queue.
// "Efter tredje kontrollen med Provsvar har inte kommit ännu eskaleras samma ärende till en namngiven operativ kö för kontroll av remiss, genomförd provtagning och överföring från LabPortalen/Webdoc. Det ska inte fortsätta återkomma automatiskt var sjätte timme."
//
// The queue is a projection of the SAME item: every lab order the doctor's
// labs lane shows as `escalated` (three "not yet", or the doctor's "Problem
// med remiss eller provtagning"). Nothing is copied or created; a registered
// result ends it in both places at once. Staffed by admin/ops, not the
// doctor, and read-only for them (TES-1040): the checklist below is what to
// check outside Ella, with the facts Ella has for each point.
export const LAB_RESULT_OPS_QUEUE_LABEL = "Saknade provsvar";

// The three points, in Emelie's order and words.
export const LAB_RESULT_OPS_CHECKLIST = [
  "Remiss",
  "Genomförd provtagning",
  "Överföring från LabPortalen/Webdoc",
] as const;

export const LAB_RESULT_ESCALATION_REASONS = [
  // The third "Provsvar har inte kommit ännu".
  "checks_exhausted",
  // The doctor chose "Problem med remiss eller provtagning".
  "problem_reported",
] as const;
export const labResultEscalationReasonSchema = z.enum(
  LAB_RESULT_ESCALATION_REASONS,
);
export type LabResultEscalationReason = z.infer<
  typeof labResultEscalationReasonSchema
>;

export const LAB_RESULT_ESCALATION_REASON_LABEL: Record<
  LabResultEscalationReason,
  string
> = {
  checks_exhausted: "Tre kontroller utan provsvar",
  problem_reported: "Läkaren har angett problem med remiss eller provtagning",
};

export const labResultEscalationRowSchema = z.object({
  // The LabOrder: the same id as the doctor's labs-lane item.
  orderId: z.string(),
  patientId: z.string(),
  patientName: z.string().nullable(),
  reason: labResultEscalationReasonSchema,
  // When the case reached the queue.
  escalatedAt: z.string(),
  check: labResultCheckSchema,
  orderedAt: z.string(),
  // "Remiss": the order's status and the references a lab can be asked about.
  remiss: z.object({
    status: z.string(),
    requisitionRef: z.string().nullable(),
    labPortalenRid: z.string().nullable(),
  }),
  // "Överföring från LabPortalen/Webdoc": LabPortalen answers that arrived
  // for this order but could not be attached (quarantine). Zero says nothing
  // unreadable arrived through LabPortalen; Webdoc has no integration to ask.
  transfer: z.object({
    quarantinedReports: z.number().int().nonnegative(),
  }),
});
export type LabResultEscalationRow = z.infer<
  typeof labResultEscalationRowSchema
>;

export const labResultEscalationListSchema = z.object({
  rows: z.array(labResultEscalationRowSchema),
});
export type LabResultEscalationList = z.infer<
  typeof labResultEscalationListSchema
>;

// Ö-473, second half: "Eventuell medicinsk patientcopy byggs som versionssatt QA-utkast och granskas av Kirill före produktion."
// A QA working assumption, NOT approved copy: shown on the patient's waiting
// surfaces (Hem card, greeting, "Dina steg") for an escalated case only when
// the API's LAB_RESULT_MISSING_PATIENT_COPY_ENABLED is true (QA). Off in
// prod until Kirill has reviewed it; a change of text bumps `version`.
export const LAB_RESULT_MISSING_PATIENT_COPY = {
  version: "tes-1401-qa-draft-1",
  sv: {
    title: "Ditt provsvar har inte kommit ännu",
    body: "Vi har inte fått ditt provsvar från labbet ännu. Vårdteamet kontrollerar remissen, provtagningen och överföringen av svaret och hör av sig om du behöver göra något.",
  },
  en: {
    title: "Your lab result has not arrived yet",
    body: "We have not received your lab result from the lab yet. The care team is checking the referral, the sampling and the transfer of the result, and will contact you if you need to do anything.",
  },
} as const;
